import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Package,
  Loader2,
  AlertCircle,
  CheckCircle,
  CreditCard,
  Wallet,
  RefreshCw,
} from 'lucide-react';
import { toast } from 'react-hot-toast';
import { loadStripe } from '@stripe/stripe-js';
import { Elements, PaymentElement, useStripe, useElements } from '@stripe/react-stripe-js';

import { TailwindDialog, DialogContent, Button, Badge } from '@/components/ui';
import { FUNCTIONS_BASE_URL } from '@/shared/config/environment';
import { FirebaseOrder } from '@/models/types';
import { BuyCreditsModal } from '@/features/admin/components/BuyCreditsModal';
import { resolveShippingMethodConfigId } from '@/shared/utils/shippingMethodConfigId';
import { useShippingMethods } from '@/features/order-journey/hooks/useShippingMethods';
import { OrderRepository } from '@/services/firebase/order-repository';
import { ShippingService } from '@/services/shipping/shipping-service';
import type { SendCloudShippingMethod } from '@/models/sendcloud';
import { applySendcloudMarkup } from '@/shared/utils/sendcloud-shipping';

// Types
interface ParcelCostEstimate {
  orderId: string;
  originalPriceCents: number;
  actualCostCents: number;
  methodName?: string;
  country?: string;
  canBeCreated: boolean;
  error?: string;
  /** Mondial Relay = 0 € plateforme ; Sendcloud = crédits / CB */
  shippingProvider?: 'sendcloud' | 'sendcloud_api_key' | 'mondial_relay';
}

interface BatchCostEstimate {
  totalOrders: number;
  validOrders: number;
  invalidOrders: number;
  totalCostCents: number;
  currentBalanceCents: number;
  canPayWithCredits: boolean;
  amountToPayCents: number;
  estimates: ParcelCostEstimate[];
}

interface BatchParcelResult {
  success: boolean;
  totalOrders: number;
  successfulParcels: number;
  failedParcels: number;
  totalCostCents: number;
  creditsUsedCents: number;
  remainingBalanceCents: number;
  results: Array<{
    orderId: string;
    success: boolean;
    trackingNumber?: string;
    error?: string;
    costCents: number;
  }>;
}

interface BatchParcelModalProps {
  open: boolean;
  onClose: () => void;
  selectedOrders: FirebaseOrder[];
  organizationId: string;
  stripePublishableKey?: string;
  onSuccess: () => void;
}

// Helpers
const formatCurrency = (cents: number) => {
  return new Intl.NumberFormat('fr-FR', {
    style: 'currency',
    currency: 'EUR',
  }).format(cents / 100);
};

/** Borne max Sendcloud souvent légèrement sous l’entier (ex. 14999 g → 14,999 kg) alors que l’UI affiche « 15 kg ». */
const SENDCLOUD_MAX_WEIGHT_EPS_KG = 0.02;

/** Extrait le message lisible des erreurs Sendcloud (validation, etc.) */
const parseSendcloudError = (errorStr: string | undefined): string => {
  if (!errorStr) return 'Erreur inconnue';
  // Format: "Sendcloud API error: 400 - {\"errors\":[{\"detail\":\"...\"}]}"
  const jsonStart = errorStr.indexOf('{');
  if (jsonStart !== -1) {
    try {
      const jsonPart = errorStr.slice(jsonStart);
      const parsed = JSON.parse(jsonPart);
      const details = parsed?.errors?.map((e: { detail?: string }) => e?.detail).filter(Boolean) || [];
      if (details.length > 0) {
        return details.join(' • ');
      }
    } catch {
      // fallback
    }
  }
  // Fallback: tronquer si trop long
  if (errorStr.length > 150) {
    return errorStr.slice(0, 147) + '...';
  }
  return errorStr;
};

// Payment Form Component
const DirectPaymentForm: React.FC<{
  onSuccess: () => void;
  onCancel: () => void;
  organizationId: string;
  paymentIntentId: string;
}> = ({ onSuccess, onCancel, organizationId, paymentIntentId }) => {
  const stripe = useStripe();
  const elements = useElements();
  const [isProcessing, setIsProcessing] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!stripe || !elements) {
      return;
    }

    setIsProcessing(true);
    setError(null);

    try {
      const { error: stripeError } = await stripe.confirmPayment({
        elements,
        confirmParams: {
          return_url: window.location.href,
        },
        redirect: 'if_required',
      });

      if (stripeError) {
        setError(stripeError.message || 'Erreur de paiement');
        setIsProcessing(false);
        return;
      }

      // Confirm and create parcels
      const response = await fetch(`${FUNCTIONS_BASE_URL}/shippingCreditsConfirmBatchPayment`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          organizationId,
          paymentIntentId,
        }),
      });

      const result = await response.json();

      if (result.success) {
        toast.success(`${result.successfulParcels} colis créés avec succès!`);
        if (result.failedParcels > 0) {
          toast.error(`${result.failedParcels} colis en échec - à réessayer`);
        }
        onSuccess();
      } else {
        throw new Error(result.error || 'Échec de la création des colis');
      }
    } catch (err: any) {
      setError(err.message);
      toast.error('Erreur lors du traitement');
    } finally {
      setIsProcessing(false);
    }
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-4">
      <PaymentElement />
      
      {error && (
        <div className="p-3 bg-red-50 border border-red-200 rounded-lg flex items-center gap-2 text-red-700 text-sm">
          <AlertCircle className="h-4 w-4" />
          {error}
        </div>
      )}

      <div className="flex gap-3 pt-4">
        <Button
          type="button"
          variant="outline"
          onClick={onCancel}
          disabled={isProcessing}
          className="flex-1"
        >
          Annuler
        </Button>
        <Button
          type="submit"
          disabled={!stripe || isProcessing}
          className="flex-1"
        >
          {isProcessing ? (
            <>
              <Loader2 className="h-4 w-4 animate-spin mr-2" />
              Traitement...
            </>
          ) : (
            'Payer et créer les colis'
          )}
        </Button>
      </div>
    </form>
  );
};

// Main Component
export const BatchParcelModal: React.FC<BatchParcelModalProps> = ({
  open,
  onClose,
  selectedOrders,
  organizationId,
  stripePublishableKey,
  onSuccess,
}) => {
  useTranslation();

  // State
  const [step, setStep] = useState<'estimate' | 'payment' | 'result'>('estimate');
  const [isLoading, setIsLoading] = useState(false);
  const [estimate, setEstimate] = useState<BatchCostEstimate | null>(null);
  const [result, setResult] = useState<BatchParcelResult | null>(null);
  const [paymentMethod, setPaymentMethod] = useState<'credits' | 'direct'>('credits');
  const dialogContentClassName = 'p-0 flex flex-col max-h-[75vh]';
  const [showPurchaseModal, setShowPurchaseModal] = useState(false);
  /** Poids (kg) pour les commandes Mondial Relay avant génération */
  const [mrWeightsKg, setMrWeightsKg] = useState<Record<string, number>>({});
  /** Ajustements Sendcloud (poids + méthode config) avant génération */
  const [sendcloudAdjustments, setSendcloudAdjustments] = useState<Record<string, { weightKg: number; sendcloudMethodId: number | null }>>({});
  const [sendcloudPlatformCatalog, setSendcloudPlatformCatalog] = useState<SendCloudShippingMethod[]>([]);
  const [sendcloudMerchantCatalog, setSendcloudMerchantCatalog] = useState<SendCloudShippingMethod[]>([]);
  const catalogRefreshDebounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const { getShippingMethodById } = useShippingMethods();

  // Stripe state for direct payment
  const [stripePromise, setStripePromise] = useState<Promise<any> | null>(null);
  const [clientSecret, setClientSecret] = useState<string | null>(null);
  const [paymentIntentId, setPaymentIntentId] = useState<string | null>(null);

  // Même logique que le tableau de bord : paid, pas d’étiquette/suivi, id méthode résolu, needsParcel !== false
  const orderIds = React.useMemo(
    () =>
      selectedOrders
        .filter(order => {
          const isPaid = order.products?.some(p => p.paymentInfo?.status === 'paid');
          const di = order.deliveryInfo;
          const hasLabelOrTracking = !!(
            order.parcelGenerated?.dateAnnounced ||
            order.parcelGenerated?.trackingUrl ||
            order.parcelGenerated?.labelPrinterUrl ||
            order.parcelGenerated?.trackingNumber
          );
          const methodConfigId = resolveShippingMethodConfigId(di ?? null);
          return (
            isPaid &&
            !hasLabelOrTracking &&
            !!methodConfigId &&
            !order.parcelGenerated
          );
        })
        .map(order => order.id!)
        .filter(Boolean),
    [selectedOrders]
  );
  const orderIdsKey = React.useMemo(() => orderIds.join(','), [orderIds]);

  const orderEmailMap = React.useMemo(() => {
    const map: Record<string, string> = {};
    selectedOrders.forEach(order => {
      if (order.id && order.orderInfo?.email) {
        map[order.id] = order.orderInfo.email;
      }
    });
    return map;
  }, [selectedOrders]);

  const sendcloudProviderNeeds = React.useMemo(() => {
    let needsPlatform = false;
    let needsMerchant = false;
    const eligible = new Set(orderIds);
    for (const o of selectedOrders) {
      if (!o?.id || !eligible.has(o.id)) continue;
      const p = String(o.deliveryInfo?.provider || '').toLowerCase();
      if (p === 'sendcloud') needsPlatform = true;
      if (p === 'sendcloud_api_key') needsMerchant = true;
    }
    return { needsPlatform, needsMerchant };
  }, [selectedOrders, orderIdsKey]);

  // Fetch estimate when modal opens
  useEffect(() => {
    if (open && orderIds.length > 0) {
      fetchEstimate();
    } else {
      setEstimate(null);
      setResult(null);
      setStep('estimate');
    }
  }, [open, orderIdsKey]);

  const refreshSendcloudCatalog = useCallback(async () => {
    const { needsPlatform, needsMerchant } = sendcloudProviderNeeds;
    if (!needsPlatform) setSendcloudPlatformCatalog([]);
    if (!needsMerchant) setSendcloudMerchantCatalog([]);
    if (!needsPlatform && !needsMerchant) return;

    const [platform, merchant] = await Promise.all([
      needsPlatform ? ShippingService.browseSendcloudMethods().catch(() => []) : Promise.resolve([]),
      needsMerchant ? ShippingService.browseSendcloudMethodsMerchant(organizationId).catch(() => []) : Promise.resolve([]),
    ]);

    if (needsPlatform) {
      setSendcloudPlatformCatalog(Array.isArray(platform) ? platform : []);
    }
    if (needsMerchant) {
      setSendcloudMerchantCatalog(Array.isArray(merchant) ? merchant : []);
    }
  }, [organizationId, sendcloudProviderNeeds]);

  const scheduleCatalogRefresh = useCallback(() => {
    if (catalogRefreshDebounceRef.current) {
      clearTimeout(catalogRefreshDebounceRef.current);
    }
    catalogRefreshDebounceRef.current = setTimeout(() => {
      if (!open) return;
      void refreshSendcloudCatalog();
    }, 350);
  }, [open, refreshSendcloudCatalog]);

  // Requête live des méthodes Sendcloud disponibles (comme CarrierFinder)
  useEffect(() => {
    if (!open) return;
    void refreshSendcloudCatalog();
  }, [open, refreshSendcloudCatalog]);

  useEffect(() => {
    return () => {
      if (catalogRefreshDebounceRef.current) {
        clearTimeout(catalogRefreshDebounceRef.current);
      }
    };
  }, []);

  // Poids par défaut pour les lignes Mondial Relay (modifiable avant création)
  useEffect(() => {
    if (!estimate) return;
    const next: Record<string, number> = {};
    for (const e of estimate.estimates) {
      if (e.shippingProvider === 'mondial_relay' && e.canBeCreated) {
        const o = selectedOrders.find(so => so.id === e.orderId);
        const raw = (o?.deliveryInfo as { minWeight?: number } | undefined)?.minWeight;
        const def = typeof raw === 'number' && raw > 0 ? raw : 0.5;
        next[e.orderId] = def;
      }
    }
    setMrWeightsKg(next);
  }, [estimate, selectedOrders]);

  /**
   * Poids explicite : évite de dépendre de `sendcloudAdjustments` dans le callback pour les effets / total.
   */
  const getCompatibleMethodsForOrder = useCallback(
    (
      order: FirebaseOrder,
      weightKg: number
    ): Array<{ id: number; label: string; price: number; name: string; description?: string }> => {
      const di: any = order.deliveryInfo || {};
      const country = String(di.country || 'FR').toUpperCase();
      const methodConfigId = resolveShippingMethodConfigId(di ?? null);
      const current = methodConfigId ? getShippingMethodById(methodConfigId) : null;
      const provider = String(di.provider || current?.provider || '').toLowerCase();
      const carrier = (current as any)?.carrier ?? (di as any)?.carrier ?? null;
      const baseIsHome =
        typeof di.isHomeDelivery === 'boolean' ? di.isHomeDelivery : Boolean(current?.isHomeDelivery);

      const liveCatalog =
        provider === 'sendcloud_api_key' ? sendcloudMerchantCatalog : sendcloudPlatformCatalog;
      if (!(provider === 'sendcloud' || provider === 'sendcloud_api_key')) return [];

      return liveCatalog
        .filter((m) => {
          if (carrier && String(m.carrier || '') !== carrier) return false;
          const isHome = String(m.servicePointInput || '').toLowerCase() !== 'none';
          if (typeof baseIsHome === 'boolean' && isHome !== baseIsHome) return false;
          if (typeof m.minWeight === 'number' && weightKg < m.minWeight) return false;
          if (typeof m.maxWeight === 'number' && m.maxWeight > 0 && weightKg > m.maxWeight + SENDCLOUD_MAX_WEIGHT_EPS_KG)
            return false;
          const countryInfo = (m.countries || []).find((c: any) => String(c.code || '').toUpperCase() === country);
          if (!countryInfo) return false;
          return true;
        })
        .map((m) => {
          const countryInfo = (m.countries || []).find((c: any) => String(c.code || '').toUpperCase() === country);
          return {
            id: Number(m.id),
            label: `${m.carrier} — ${m.name}`,
            price: Number(countryInfo?.price || 0),
            name: m.name,
            description: m.description,
          };
        });
    },
    [sendcloudPlatformCatalog, sendcloudMerchantCatalog, getShippingMethodById]
  );

  /**
   * Sans `deliveryInfo.methodId`, le <select> peut afficher la 1re option alors que l’état a `sendcloudMethodId: null`
   * → le total reste sur l’estimation backend. On aligne l’état sur le catalogue dès qu’il est chargé (et quand le poids change).
   */
  useEffect(() => {
    if (!estimate) return;
    const { needsPlatform, needsMerchant } = sendcloudProviderNeeds;
    if (needsPlatform && sendcloudPlatformCatalog.length === 0) return;
    if (needsMerchant && sendcloudMerchantCatalog.length === 0) return;

    setSendcloudAdjustments(prev => {
      const next = { ...prev };
      let changed = false;

      for (const e of estimate.estimates) {
        if (!(e.shippingProvider === 'sendcloud' || e.shippingProvider === 'sendcloud_api_key')) continue;
        if (!e.canBeCreated) continue;
        const o = selectedOrders.find(so => so.id === e.orderId);
        if (!o?.id) continue;

        const di: any = o.deliveryInfo || {};
        const rawW = Number(di.minWeight ?? di.weight ?? 0.5) || 0.5;
        const scIdFromOrder = Number(di.methodId);
        const existing = prev[o.id];
        const weightKg = existing?.weightKg ?? rawW;
        let methodId: number | null =
          existing?.sendcloudMethodId != null
            ? existing.sendcloudMethodId
            : Number.isFinite(scIdFromOrder)
              ? scIdFromOrder
              : null;

        const compatible = getCompatibleMethodsForOrder(o, weightKg);
        const ids = new Set(compatible.map(m => m.id));
        let nextMethodId = methodId;
        if (compatible.length > 0 && (nextMethodId == null || !ids.has(nextMethodId))) {
          nextMethodId = compatible[0].id;
        }

        const before = prev[o.id];
        const after = { weightKg, sendcloudMethodId: nextMethodId };
        next[o.id] = after;
        if (!before || before.weightKg !== after.weightKg || before.sendcloudMethodId !== after.sendcloudMethodId) {
          changed = true;
        }
      }

      return changed ? next : prev;
    });
  }, [
    estimate,
    selectedOrders,
    sendcloudPlatformCatalog,
    sendcloudMerchantCatalog,
    sendcloudProviderNeeds,
    getCompatibleMethodsForOrder,
    sendcloudAdjustments,
  ]);

  const effectiveTotalCostCents = React.useMemo(() => {
    if (!estimate) return 0;
    let total = 0;
    for (const e of estimate.estimates) {
      if (!e.canBeCreated) continue;
      if (e.shippingProvider === 'sendcloud' || e.shippingProvider === 'sendcloud_api_key') {
        const o = selectedOrders.find((so) => so.id === e.orderId);
        const adj = o?.id ? sendcloudAdjustments[o.id] : undefined;
        if (!o || !adj?.sendcloudMethodId) {
          total += e.actualCostCents || 0;
          continue;
        }
        const weightKg =
          sendcloudAdjustments[o.id]?.weightKg ??
          (Number((o.deliveryInfo as any)?.minWeight ?? (o.deliveryInfo as any)?.weight ?? 0.5) || 0.5);
        let priceEuros: number | undefined = getCompatibleMethodsForOrder(o, weightKg).find(
          (m) => m.id === adj.sendcloudMethodId
        )?.price;
        if (priceEuros == null || !Number.isFinite(priceEuros)) {
          const di: any = o.deliveryInfo || {};
          const methodConfigId = resolveShippingMethodConfigId(di ?? null);
          const current = methodConfigId ? getShippingMethodById(methodConfigId) : null;
          const provider = String(di.provider || current?.provider || '').toLowerCase();
          const liveCatalog =
            provider === 'sendcloud_api_key' ? sendcloudMerchantCatalog : sendcloudPlatformCatalog;
          const m = liveCatalog.find((x) => Number(x.id) === Number(adj.sendcloudMethodId));
          const country = String(di.country || 'FR').toUpperCase();
          const countryInfo = m
            ? (m.countries || []).find((c: any) => String(c.code || '').toUpperCase() === country)
            : undefined;
          if (countryInfo) priceEuros = Number(countryInfo.price || 0);
        }
        if (priceEuros != null && Number.isFinite(priceEuros)) {
          const euros =
            e.shippingProvider === 'sendcloud_api_key' ? Number(priceEuros) || 0 : applySendcloudMarkup(priceEuros);
          total += Math.round(euros * 100);
          continue;
        }
        total += e.actualCostCents || 0;
        continue;
      }
      total += e.actualCostCents || 0;
    }
    return total;
  }, [estimate, selectedOrders, sendcloudAdjustments, sendcloudPlatformCatalog, sendcloudMerchantCatalog, getShippingMethodById, getCompatibleMethodsForOrder]);

  const canPayWithCreditsEffective = React.useMemo(
    () => (estimate ? effectiveTotalCostCents <= (estimate.currentBalanceCents || 0) : false),
    [estimate, effectiveTotalCostCents]
  );

  const amountToPayEffective = React.useMemo(
    () => (estimate ? Math.max(effectiveTotalCostCents - (estimate.currentBalanceCents || 0), 0) : 0),
    [estimate, effectiveTotalCostCents]
  );

  /**
   * Paiement CB CashLive requis uniquement pour les méthodes Sendcloud plateforme.
   * Les méthodes sendcloud_api_key / mondial_relay se créent sans paiement carte ici.
   */
  const requiresPlatformPayment = React.useMemo(() => {
    if (!estimate) return false;
    return estimate.estimates.some(
      (e) => e.canBeCreated && e.shippingProvider === 'sendcloud' && (e.actualCostCents || 0) > 0
    );
  }, [estimate]);

  /** Libellé du total : plateforme = marge ; compte marchand = tarif brut. */
  const batchTotalCostLabel = React.useMemo(() => {
    if (!estimate) return 'Total estimé';
    const eligible = estimate.estimates.filter(e => e.canBeCreated);
    const hasPlat = eligible.some(e => e.shippingProvider === 'sendcloud');
    const hasKey = eligible.some(e => e.shippingProvider === 'sendcloud_api_key');
    if (hasPlat && hasKey) return 'Total estimé';
    if (hasKey) return 'Total estimé (Sendcloud — compte marchand, sans marge)';
    if (hasPlat) return 'Total estimé (Sendcloud — plateforme + marge)';
    return 'Total estimé';
  }, [estimate]);

  const persistSendcloudAdjustment = async (order: FirebaseOrder, sendcloudMethodId: number, weightKg: number): Promise<void> => {
    const di: any = order.deliveryInfo || {};
    const country = String(di.country || 'FR');
    const methodConfigId = resolveShippingMethodConfigId(di ?? null);
    if (!methodConfigId) throw new Error('Méthode de livraison config introuvable');
    const cfgMethod = getShippingMethodById(methodConfigId);
    const provider = String(di.provider || cfgMethod?.provider || '').toLowerCase();
    const carrierLocked = (cfgMethod as any)?.carrier ?? (di as any)?.carrier ?? null;
    const baseIsHome =
      typeof di.isHomeDelivery === 'boolean' ? di.isHomeDelivery : Boolean(cfgMethod?.isHomeDelivery);

    const liveCatalog =
      provider === 'sendcloud_api_key' ? sendcloudMerchantCatalog : sendcloudPlatformCatalog;
    const live = liveCatalog.find((m) => Number(m.id) === Number(sendcloudMethodId));
    if (!live) throw new Error('Méthode Sendcloud introuvable');
    if (carrierLocked && String(live.carrier || '') !== carrierLocked) throw new Error('Carrier invalide');
    const liveIsHome = String(live.servicePointInput || '').toLowerCase() !== 'none';
    if (typeof baseIsHome === 'boolean' && liveIsHome !== baseIsHome) throw new Error('Type de livraison invalide');
    if (typeof live.minWeight === 'number' && weightKg < live.minWeight) throw new Error('Poids trop faible pour cette méthode');
    if (typeof live.maxWeight === 'number' && live.maxWeight > 0 && weightKg > live.maxWeight + SENDCLOUD_MAX_WEIGHT_EPS_KG)
      throw new Error('Poids trop élevé pour cette méthode');
    const countryInfo = (live.countries || []).find((c: any) => String(c.code || '').toUpperCase() === String(country).toUpperCase());
    if (!countryInfo) throw new Error('Pays non disponible pour cette méthode');

    await OrderRepository.updateDeliveryInfo({
      orderId: order.id!,
      deliveryMethod: methodConfigId,
      address: di.address || '',
      addressLine2: di.addressLine2 || undefined,
      fullAddress: di.fullAddress || undefined,
      houseNumber: di.houseNumber || '',
      postalCode: di.postalCode || '',
      city: di.city || '',
      country,
      pickupPoint: liveIsHome ? undefined : (di.pickupPoint || undefined),
      weight: weightKg,
      minWeight: weightKg,
      methodId: Number(sendcloudMethodId),
      methodName: live.name,
      methodDescription: live.description,
      isHomeDelivery: liveIsHome,
      servicePointInput: di.servicePointInput,
      shippingCost: Number(countryInfo?.price || 0),
      estimatedHours: Number(countryInfo?.lead_time_hours || di.estimatedHours || 72),
      provider: provider as any,
      methodPrice: Number(countryInfo?.price || 0),
      methodCurrency: 'EUR',
      methodStatus: 'active',
    });
  };

  const fetchEstimate = async () => {
    setIsLoading(true);
    try {
      const response = await fetch(`${FUNCTIONS_BASE_URL}/shippingCreditsEstimateBatch`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          organizationId,
          orderIds,
        }),
      });

      const data = await response.json();
      if (data.success) {
        const est = data.estimate as BatchCostEstimate;
        setEstimate(est);
        if (est.totalCostCents <= 0) {
          setPaymentMethod('credits');
        } else if (est.canPayWithCredits) {
          setPaymentMethod('credits');
        } else {
          setPaymentMethod('direct');
        }
      } else {
        throw new Error(data.error || 'Erreur lors de l\'estimation');
      }
    } catch (error: any) {
      console.error('Error fetching estimate:', error);
      toast.error(error.message || 'Erreur lors de l\'estimation');
    } finally {
      setIsLoading(false);
    }
  };

  // Create parcels with credits (ou génération « gratuite » plateforme si seulement MR / coût 0)
  const handleCreateWithCredits = async () => {
    if (!estimate) return;
    // Ne bloquer sur les crédits que s'il y a réellement un coût plateforme à débiter.
    if (requiresPlatformPayment && effectiveTotalCostCents > 0 && !canPayWithCreditsEffective) {
      toast.error('Crédits insuffisants pour les envois Sendcloud plateforme.');
      return;
    }

    setIsLoading(true);
    try {
      // Appliquer les ajustements Sendcloud (poids + méthode) avant génération
      const sendcloudEstimates = estimate.estimates.filter(
        (e) => (e.shippingProvider === 'sendcloud' || e.shippingProvider === 'sendcloud_api_key') && e.canBeCreated
      );
      for (const e of sendcloudEstimates) {
        const o = selectedOrders.find((so) => so.id === e.orderId);
        const adj = o?.id ? sendcloudAdjustments[o.id] : undefined;
        if (!o || !adj) continue;
        if (!adj.sendcloudMethodId) continue;
        await persistSendcloudAdjustment(o, adj.sendcloudMethodId, adj.weightKg);
      }

      const weightsByOrderId: Record<string, number> = {};
      for (const e of estimate.estimates) {
        if (e.shippingProvider === 'mondial_relay' && e.canBeCreated) {
          const w = mrWeightsKg[e.orderId];
          if (w != null && Number.isFinite(w) && w > 0) {
            weightsByOrderId[e.orderId] = w;
          }
        }
      }

      const response = await fetch(`${FUNCTIONS_BASE_URL}/shippingCreditsCreateParcels`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          organizationId,
          orderIds: estimate.estimates.filter(e => e.canBeCreated).map(e => e.orderId),
          weightsByOrderId:
            Object.keys(weightsByOrderId).length > 0 ? weightsByOrderId : undefined,
        }),
      });

      const data = await response.json();

      // Erreur HTTP ou erreur globale (pas de results)
      if (!response.ok || (data.error && !data.results?.length)) {
        throw new Error(data.error || data.details || 'Erreur lors de la création des colis');
      }

      // Toujours afficher le résultat (succès partiel ou total échec)
      setResult(data);
      setStep('result');
      if (data.successfulParcels > 0) {
        toast.success(`${data.successfulParcels} colis créés avec succès!`);
      }
      if (data.failedParcels > 0) {
        toast.error(`${data.failedParcels} colis en échec`);
      }
      onSuccess();
    } catch (error: any) {
      console.error('Error creating parcels:', error);
      toast.error(error.message || 'Erreur lors de la création des colis');
    } finally {
      setIsLoading(false);
    }
  };

  // Start direct payment flow
  const handleStartDirectPayment = async () => {
    if (!estimate) return;
    if (!requiresPlatformPayment || effectiveTotalCostCents <= 0) {
      toast('Aucun paiement carte requis pour ce lot. Création directe lancée.');
      await handleCreateWithCredits();
      return;
    }

    setIsLoading(true);
    try {
      // Appliquer les ajustements Sendcloud avant de calculer/créer le paiement
      if (estimate) {
        const sendcloudEstimates = estimate.estimates.filter(
          (e) => (e.shippingProvider === 'sendcloud' || e.shippingProvider === 'sendcloud_api_key') && e.canBeCreated
        );
        for (const e of sendcloudEstimates) {
          const o = selectedOrders.find((so) => so.id === e.orderId);
          const adj = o?.id ? sendcloudAdjustments[o.id] : undefined;
          if (!o || !adj) continue;
          if (!adj.sendcloudMethodId) continue;
          await persistSendcloudAdjustment(o, adj.sendcloudMethodId, adj.weightKg);
        }
      }

      const response = await fetch(`${FUNCTIONS_BASE_URL}/shippingCreditsCreateBatchPayment`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          organizationId,
          orderIds: estimate.estimates.filter(e => e.canBeCreated).map(e => e.orderId),
        }),
      });

      const data = await response.json();
      if (data.success) {
        if (data.stripePublishableKey || stripePublishableKey) {
          setStripePromise(loadStripe(data.stripePublishableKey || stripePublishableKey));
        }
        setClientSecret(data.clientSecret);
        setPaymentIntentId(data.paymentIntentId);
        setStep('payment');
      } else {
        throw new Error(data.error || 'Erreur lors de la création du paiement');
      }
    } catch (error: any) {
      console.error('Error creating payment:', error);
      toast.error(error.message || 'Erreur lors de la création du paiement');
    } finally {
      setIsLoading(false);
    }
  };

  // Fermer la modal sans annuler la sélection (juste reset les states internes)
  const handleClose = () => {
    setStep('estimate');
    setEstimate(null);
    setResult(null);
    setClientSecret(null);
    setPaymentIntentId(null);
    // Appeler onClose uniquement pour fermer la modal, pas pour désélectionner
    onClose();
  };
  
  // Fermer après succès - c'est là qu'on peut désélectionner si nécessaire
  const handleCloseAfterSuccess = () => {
    setStep('estimate');
    setEstimate(null);
    setResult(null);
    setClientSecret(null);
    setPaymentIntentId(null);
    onSuccess(); // Refresh la liste
    onClose();
  };

  // No valid orders
  if (open && orderIds.length === 0) {
    return (
      <TailwindDialog
        isOpen={open}
        onClose={handleClose}
        title="Aucune commande éligible"
        maxWidth="sm"
        variant="themed"
      >
        <DialogContent>
          <div className="text-center py-6">
            <div className="inline-flex p-4 rounded-full bg-warning-10 mb-4">
              <AlertCircle className="h-8 w-8 text-warning" />
            </div>
            <h2 className="text-xl font-semibold text-foreground mb-2">
              Aucune commande éligible
            </h2>
            <p className="text-foreground-secondary text-sm mb-4">
              Les commandes sélectionnées n'ont pas besoin de génération de colis
              (soit déjà créés, soit non payées, soit sans méthode de livraison).
            </p>
            <Button onClick={handleClose} variant="outline">
              Fermer
            </Button>
          </div>
        </DialogContent>
      </TailwindDialog>
    );
  }

  return (
    <>
    <TailwindDialog
      isOpen={open}
      onClose={handleClose}
      title="Génération de colis"
      maxWidth="xl"
      showCloseButton={true}
      variant="themed"
    >
      <DialogContent className={dialogContentClassName}>
        {/* Step: Estimate */}
        {step === 'estimate' && (
          <>
            {isLoading ? (
              <div className="py-12 text-center">
                <Loader2 className="h-8 w-8 animate-spin text-primary mx-auto mb-3" />
                <p className="text-foreground-secondary">Calcul en cours...</p>
              </div>
            ) : estimate ? (
              <>
              <div className="flex-1 overflow-y-auto p-4 space-y-3">
                {/* Info crédits : uniquement si Sendcloud plateforme présent dans le lot */}
                {estimate.estimates.some(e => e.canBeCreated && e.shippingProvider === 'sendcloud') && (
                  <div className="bg-gradient-to-r from-primary-5 to-primary-10 border border-primary-20 rounded-lg p-2.5">
                    <div className="flex items-center gap-2 justify-between">
                      <div className="flex items-center gap-2">
                        <Wallet className="w-4 h-4 text-primary flex-shrink-0" />
                        <p className="text-xs font-medium text-primary">
                          💡 Les crédits concernent uniquement Sendcloud plateforme (pas Sendcloud API key / Mondial Relay direct).
                        </p>
                      </div>
                      <Button
                        size="sm"
                        onClick={() => setShowPurchaseModal(true)}
                        className="text-xs py-1 px-2 h-auto whitespace-nowrap"
                      >
                        Recharger
                      </Button>
                    </div>
                  </div>
                )}

                {/* Summary - Plus compact */}
                <div className="grid grid-cols-2 gap-3">
                  <div className="bg-background-secondary rounded-lg p-3 border border-border">
                    <p className="text-xs text-foreground-secondary mb-0.5">Colis à créer</p>
                    <p className="text-xl font-bold text-foreground">
                      {estimate.validOrders}
                      {estimate.invalidOrders > 0 && (
                        <span className="text-xs font-normal text-warning ml-1">
                          ({estimate.invalidOrders})
                        </span>
                      )}
                    </p>
                  </div>
                  <div className="bg-background-secondary rounded-lg p-3 border border-border">
                    <p className="text-xs text-foreground-secondary mb-0.5">{batchTotalCostLabel}</p>
                    <p className="text-xl font-bold text-foreground">
                      {formatCurrency(effectiveTotalCostCents)}
                    </p>
                    {effectiveTotalCostCents <= 0 && (
                      <p className="text-[10px] text-foreground-secondary mt-1">
                        Mondial Relay : pas de débit crédits / CB ici
                      </p>
                    )}
                  </div>
                </div>

                {estimate.estimates.some(e => !e.canBeCreated && e.error) && (
                  <div className="rounded-lg border border-warning/40 bg-warning-5 p-3">
                    <p className="text-xs font-semibold text-warning-dark flex items-center gap-2">
                      <AlertCircle className="h-4 w-4 shrink-0" />
                      Commandes exclues de la génération
                    </p>
                    <ul className="mt-2 space-y-1.5 text-xs text-foreground-secondary list-none pl-0">
                      {estimate.estimates
                        .filter(e => !e.canBeCreated && e.error)
                        .map(e => (
                          <li key={e.orderId} className="leading-snug">
                            <span className="font-medium text-foreground">
                              {orderEmailMap[e.orderId] || e.orderId.slice(-8)}
                            </span>
                            <span className="text-foreground-secondary"> — </span>
                            <span>{parseSendcloudError(e.error)}</span>
                          </li>
                        ))}
                    </ul>
                  </div>
                )}

                {estimate.estimates.some(
                  e => e.shippingProvider === 'mondial_relay' && e.canBeCreated,
                ) && (
                  <div className="border border-border rounded-lg p-3 space-y-2 bg-background-secondary/40">
                    <p className="text-xs font-medium text-foreground">Poids Mondial Relay (kg)</p>
                    <p className="text-[10px] text-foreground-secondary">
                      Ajustez avant génération ; le colis est facturé sur votre contrat Mondial Relay.
                    </p>
                    <div className="space-y-2 max-h-36 overflow-y-auto">
                      {estimate.estimates
                        .filter(e => e.shippingProvider === 'mondial_relay' && e.canBeCreated)
                        .map(e => (
                          <div
                            key={e.orderId}
                            className="flex items-center gap-2 text-xs"
                          >
                            <span className="flex-1 truncate text-foreground-secondary">
                              {orderEmailMap[e.orderId] || e.orderId.slice(-8)}
                            </span>
                            <input
                              type="number"
                              min={0.01}
                              step={0.1}
                              className="w-20 px-2 py-1 border border-border rounded-md bg-surface text-foreground text-right"
                              value={mrWeightsKg[e.orderId] ?? ''}
                              onChange={ev => {
                                const v = parseFloat(ev.target.value);
                                setMrWeightsKg(prev => ({
                                  ...prev,
                                  [e.orderId]: Number.isFinite(v) ? v : prev[e.orderId] ?? 0.5,
                                }));
                              }}
                            />
                          </div>
                        ))}
                    </div>
                  </div>
                )}

        {estimate.estimates.some(
          e => (e.shippingProvider === 'sendcloud' || e.shippingProvider === 'sendcloud_api_key') && e.canBeCreated,
        ) && (
          <div className="border border-border rounded-lg p-3 space-y-2 bg-background-secondary/40">
            <p className="text-xs font-medium text-foreground">Ajustements Sendcloud (poids + méthode)</p>
            <p className="text-[10px] text-foreground-secondary">
              Filtré par pays, poids, type (domicile/relais) et même carrier.
            </p>
            <div className="space-y-2 max-h-44 overflow-y-auto">
              {estimate.estimates
                .filter(e => (e.shippingProvider === 'sendcloud' || e.shippingProvider === 'sendcloud_api_key') && e.canBeCreated)
                .map(e => {
                  const o = selectedOrders.find(so => so.id === e.orderId);
                  if (!o?.id) return null;
                  const di: any = o.deliveryInfo || {};
                  const provider = String(di.provider || '').toLowerCase();
                  if (!(provider === 'sendcloud' || provider === 'sendcloud_api_key')) return null;
                  const rowPriceEuros = (raw: number) =>
                    provider === 'sendcloud_api_key' ? raw : applySendcloudMarkup(raw);
                  const currentAdj = sendcloudAdjustments[o.id] || { weightKg: Number(di.minWeight ?? di.weight ?? 0.5) || 0.5, sendcloudMethodId: Number.isFinite(Number(di.methodId)) ? Number(di.methodId) : null };
                  const compatible = getCompatibleMethodsForOrder(o, currentAdj.weightKg);
                  const selectedMethod = compatible.find((m) => m.id === Number(currentAdj.sendcloudMethodId));
                  const selectedMethodPrice = selectedMethod?.price;
                  const countryCode = String(di.country || 'FR').toUpperCase();
                  const liveCatalog = String(di.provider || '').toLowerCase() === 'sendcloud_api_key' ? sendcloudMerchantCatalog : sendcloudPlatformCatalog;
                  const selectedLive = liveCatalog.find((m) => Number(m.id) === Number(currentAdj.sendcloudMethodId));
                  const min = (selectedLive as any)?.minWeight;
                  const max = (selectedLive as any)?.maxWeight;
                  const outOfRange =
                    (typeof min === 'number' && Number.isFinite(min) && currentAdj.weightKg < min) ||
                    (typeof max === 'number' &&
                      Number.isFinite(max) &&
                      currentAdj.weightKg > max + SENDCLOUD_MAX_WEIGHT_EPS_KG) ||
                    !compatible.some((m) => m.id === Number(currentAdj.sendcloudMethodId));

                  return (
                    <div key={e.orderId} className="rounded-md border border-border bg-surface p-2.5">
                      <div className="flex items-center justify-between gap-2">
                        <span className="text-xs font-medium text-foreground truncate">
                          {orderEmailMap[e.orderId] || e.orderId.slice(-8)}
                        </span>
                        <span className={`text-[10px] font-semibold ${provider === 'sendcloud_api_key' ? 'text-foreground-secondary' : 'text-primary'}`}>
                          {provider === 'sendcloud_api_key' ? 'Sendcloud API key' : 'Sendcloud'}
                        </span>
                      </div>
                      <div className="mt-2 grid grid-cols-12 gap-2 items-center">
                        <div className="col-span-4">
                          <label className="block text-[10px] text-foreground-secondary mb-1">Poids (kg)</label>
                          <input
                            type="number"
                            min={0.01}
                            step={0.1}
                            className={`w-full px-2 py-1 border rounded-md bg-background-secondary text-foreground text-right ${outOfRange ? 'border-warning' : 'border-border'}`}
                            value={currentAdj.weightKg}
                            onChange={ev => {
                              const v = Number.parseFloat(ev.target.value);
                              setSendcloudAdjustments(prev => ({
                                ...prev,
                                [o.id!]: {
                                  weightKg: Number.isFinite(v) ? v : prev[o.id!]?.weightKg ?? 0.5,
                                  sendcloudMethodId: prev[o.id!]?.sendcloudMethodId ?? currentAdj.sendcloudMethodId,
                                },
                              }));
                              scheduleCatalogRefresh();
                            }}
                          />
                        </div>
                        <div className="col-span-8">
                          <label className="block text-[10px] text-foreground-secondary mb-1">Méthode</label>
                          <select
                            className={`w-full px-2 py-1 border rounded-md bg-background-secondary text-foreground ${outOfRange ? 'border-warning' : 'border-border'}`}
                            value={currentAdj.sendcloudMethodId ?? ''}
                            onChange={(ev) => {
                              const mid = Number(ev.target.value);
                              setSendcloudAdjustments(prev => ({
                                ...prev,
                                [o.id!]: {
                                  weightKg: prev[o.id!]?.weightKg ?? currentAdj.weightKg,
                                  sendcloudMethodId: Number.isFinite(mid) ? mid : null,
                                },
                              }));
                            }}
                          >
                            {compatible.map(m => (
                              <option key={String(m.id)} value={m.id}>
                                {m.label} — {rowPriceEuros(m.price).toFixed(2)}€
                              </option>
                            ))}
                          </select>
                          <p className="mt-1 text-[10px] text-foreground-secondary">
                            Prix {countryCode} (
                            {provider === 'sendcloud_api_key' ? 'tarif marchand' : 'avec marge plateforme'}
                            ):{' '}
                            {typeof selectedMethodPrice === 'number'
                              ? `${rowPriceEuros(selectedMethodPrice).toFixed(2)}€`
                              : '—'}
                          </p>
                          {outOfRange && (
                            <p className="mt-1 text-[10px] text-warning-dark">
                              Poids hors plage : choisissez une méthode compatible.
                            </p>
                          )}
                        </div>
                      </div>
                    </div>
                  );
                })}
            </div>
          </div>
        )}

                {/* Paiement : uniquement si coût Sendcloud plateforme > 0 */}
                {requiresPlatformPayment && effectiveTotalCostCents > 0 ? (
                <div className="space-y-2">
                  <p className="text-xs font-medium text-foreground">Mode de paiement</p>
                  <div className="grid grid-cols-2 gap-2">
                    <button
                      type="button"
                      onClick={() => setPaymentMethod('credits')}
                      disabled={!canPayWithCreditsEffective}
                      className={`p-2.5 rounded-lg border-2 transition-all text-left ${
                        paymentMethod === 'credits'
                          ? 'border-primary bg-primary-10'
                          : canPayWithCreditsEffective
                          ? 'border-border hover:border-primary-50'
                          : 'border-border bg-background-secondary opacity-60 cursor-not-allowed'
                      }`}
                    >
                      <div className="flex items-center justify-between mb-1">
                        <Wallet className={`h-4 w-4 ${paymentMethod === 'credits' ? 'text-primary' : 'text-foreground-secondary'}`} />
                        {canPayWithCreditsEffective ? (
                          <Badge variant="success" className="text-[10px] py-0 px-1.5">OK</Badge>
                        ) : (
                          <Badge variant="warning" className="text-[10px] py-0 px-1.5">Insuffisant</Badge>
                        )}
                      </div>
                      <p className="text-xs font-medium text-foreground">Crédits</p>
                      <p className={`text-[10px] mt-0.5 font-medium ${canPayWithCreditsEffective ? 'text-success' : 'text-error'}`}>
                        {formatCurrency(estimate.currentBalanceCents)} dispo
                        {!canPayWithCreditsEffective && (
                          <span className="text-error font-semibold"> • -{formatCurrency(amountToPayEffective)}</span>
                        )}
                      </p>
                    </button>
                    <button
                      type="button"
                      onClick={() => setPaymentMethod('direct')}
                      className={`p-2.5 rounded-lg border-2 transition-all text-left ${
                        paymentMethod === 'direct'
                          ? 'border-primary bg-primary-10'
                          : 'border-border hover:border-primary-50'
                      }`}
                    >
                      <div className="flex items-center justify-between mb-1">
                        <CreditCard className={`h-4 w-4 ${paymentMethod === 'direct' ? 'text-primary' : 'text-foreground-secondary'}`} />
                      </div>
                      <p className="text-xs font-medium text-foreground">Carte bancaire</p>
                      <p className="text-[10px] text-foreground-secondary mt-0.5">
                        Paiement direct
                      </p>
                    </button>
                  </div>
                </div>
                ) : (
                  <p className="text-xs text-foreground-secondary rounded-lg border border-border bg-background-secondary/30 p-2.5">
                    Aucun montant à régler sur CashLive pour ce lot : génération directe (sans carte bancaire).
                  </p>
                )}
              </div>
              
              {/* Footer with fixed buttons - Plus compact */}
              <div className="border-t border-border p-3 bg-surface flex gap-2">
                <Button variant="outline" onClick={handleClose} className="flex-1 text-sm py-2">
                  Annuler
                </Button>
                {!requiresPlatformPayment || effectiveTotalCostCents <= 0 || paymentMethod === 'credits' ? (
                  <Button
                    onClick={handleCreateWithCredits}
                    disabled={
                      estimate.validOrders === 0 ||
                      (requiresPlatformPayment && effectiveTotalCostCents > 0 && !canPayWithCreditsEffective)
                    }
                    leftIcon={<Package className="h-3.5 w-3.5" />}
                    className="flex-1 text-sm py-2"
                  >
                    {!requiresPlatformPayment || effectiveTotalCostCents <= 0
                      ? `Générer (${estimate.validOrders})`
                      : `Créer (${estimate.validOrders})`}
                  </Button>
                ) : (
                  <Button
                    onClick={handleStartDirectPayment}
                    disabled={estimate.validOrders === 0 || effectiveTotalCostCents <= 0}
                    leftIcon={<CreditCard className="h-3.5 w-3.5" />}
                    className="flex-1 text-sm py-2"
                  >
                    Payer {formatCurrency(effectiveTotalCostCents)}
                  </Button>
                )}
              </div>
              </>
            ) : (
              <div className="py-12 text-center">
                <AlertCircle className="h-8 w-8 text-error mx-auto mb-3" />
                <p className="text-foreground-secondary">Erreur lors de l'estimation</p>
                <Button variant="outline" onClick={fetchEstimate} className="mt-4">
                  <RefreshCw className="h-4 w-4 mr-2" />
                  Réessayer
                </Button>
              </div>
            )}
          </>
        )}

        {/* Step: Payment */}
        {step === 'payment' && stripePromise && clientSecret && (
          <div className="flex-1 overflow-y-auto p-6 space-y-4">
            <div className="bg-background-secondary rounded-lg p-4 text-center mb-4">
              <p className="text-sm text-foreground-secondary">Montant à payer</p>
              <p className="text-2xl font-bold text-foreground">
                {estimate ? formatCurrency(effectiveTotalCostCents) : '—'}
              </p>
            </div>
            <Elements stripe={stripePromise} options={{ clientSecret }}>
              <DirectPaymentForm
                onSuccess={handleCloseAfterSuccess}
                onCancel={() => setStep('estimate')}
                organizationId={organizationId}
                paymentIntentId={paymentIntentId!}
              />
            </Elements>
          </div>
        )}

        {/* Step: Result - Plus compact */}
        {step === 'result' && result && (
          <>
          <div className="flex-1 overflow-y-auto p-4 space-y-3">
            <div className="text-center py-2">
              {result.failedParcels === 0 ? (
                <>
                  <div className="inline-flex p-2 rounded-full bg-success-10 mb-2">
                    <CheckCircle className="h-6 w-6 text-success" />
                  </div>
                  <h3 className="text-base font-semibold text-foreground">
                    Tous les colis créés !
                  </h3>
                </>
              ) : result.successfulParcels > 0 ? (
                <>
                  <div className="inline-flex p-2 rounded-full bg-warning-10 mb-2">
                    <AlertCircle className="h-6 w-6 text-warning" />
                  </div>
                  <h3 className="text-base font-semibold text-foreground">
                    Création partielle
                  </h3>
                </>
              ) : (
                <>
                  <div className="inline-flex p-2 rounded-full bg-error-10 mb-2">
                    <AlertCircle className="h-6 w-6 text-error" />
                  </div>
                  <h3 className="text-base font-semibold text-foreground">
                    Échec de la création
                  </h3>
                </>
              )}
            </div>

            <div className="grid grid-cols-3 gap-2">
              <div className="bg-success-10 rounded-lg p-2 text-center">
                <p className="text-xl font-bold text-success">{result.successfulParcels}</p>
                <p className="text-[10px] text-success">Réussis</p>
              </div>
              <div className="bg-error-10 rounded-lg p-2 text-center">
                <p className="text-xl font-bold text-error">{result.failedParcels}</p>
                <p className="text-[10px] text-error">Échecs</p>
              </div>
              <div className="bg-primary-10 rounded-lg p-2 text-center">
                <p className="text-xl font-bold text-primary">
                  {formatCurrency(result.remainingBalanceCents)}
                </p>
                <p className="text-[10px] text-primary">Solde</p>
              </div>
            </div>

            {/* Result details - Plus compact, erreurs lisibles */}
            <div className="border border-border rounded-lg max-h-56 overflow-y-auto">
              {result.results.map((r) => {
                const email = orderEmailMap[r.orderId] || r.orderId.slice(-8);
                const displayError = r.error ? parseSendcloudError(r.error) : null;
                return (
                  <div
                    key={r.orderId}
                    className={`px-3 py-2 border-b border-border last:border-b-0 hover:bg-background-secondary/50 ${!r.success && displayError ? 'bg-error-5' : ''}`}
                  >
                    <div className="flex items-start gap-2">
                      {r.success ? (
                        <CheckCircle className="h-3.5 w-3.5 text-success flex-shrink-0 mt-0.5" />
                      ) : (
                        <AlertCircle className="h-3.5 w-3.5 text-error flex-shrink-0 mt-0.5" />
                      )}
                      <div className="flex-1 min-w-0">
                        <span className="text-xs text-foreground truncate block">{email}</span>
                        {r.success && r.trackingNumber ? (
                          <span className="text-[10px] font-mono text-success bg-success-10 px-1.5 py-0.5 rounded inline-block mt-0.5">
                            {r.trackingNumber}
                          </span>
                        ) : displayError ? (
                          <p className="text-xs text-error mt-0.5 break-words" title={r.error}>
                            {displayError}
                          </p>
                        ) : null}
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
          
          {/* Footer with fixed button - Plus compact */}
          <div className="border-t border-border p-3 bg-surface">
            <Button onClick={handleCloseAfterSuccess} className="w-full text-sm py-2">
              Fermer
            </Button>
          </div>
          </>
        )}
      </DialogContent>
    </TailwindDialog>

    {/* Purchase Modal */}
    <BuyCreditsModal
      isOpen={showPurchaseModal}
      onClose={() => setShowPurchaseModal(false)}
      organizationId={organizationId}
      currentBalance={estimate?.currentBalanceCents ?? 0}
      onSuccess={() => {
        setShowPurchaseModal(false);
        // Refresh estimate after purchase
        fetchEstimate();
      }}
    />
  </>
  );
};

export default BatchParcelModal;


